import React, { useState, useEffect } from "react";
import Modal from "antd/es/modal/Modal";

function clsx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  disabled = false,
  readOnly = false,
  help,
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-rose-600">*</span>}
        {readOnly && (
          <span className="text-xs text-gray-400"> (solo lectura)</span>
        )}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled || readOnly}
        readOnly={readOnly}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      />
      {help && <span className="block text-xs text-gray-400 mt-1">{help}</span>}
    </label>
  );
}

/**
 * Abre un PDF en una nueva pestaña.
 * Ahora soporta tanto URLs de Supabase como base64 legacy.
 * @param {string} pdfSource - URL o base64 del PDF
 */
function openPdfInNewTab(pdfSource) {
  if (!pdfSource) return;
  if (typeof window === "undefined") return;

  try {
    // Si es una URL directa (HTTP/HTTPS), abrirla directamente
    if (pdfSource.startsWith("http://") || pdfSource.startsWith("https://")) {
      window.open(pdfSource, "_blank", "noopener,noreferrer");
      return;
    }

    // Si es base64, procesarlo como antes
    let mime = "application/pdf";
    let base64 = pdfSource;

    // Si viene como data:application/pdf;base64,XXXXX...
    const match = pdfSource.match(/^data:(.+);base64,(.+)$/);
    if (match) {
      mime = match[1] || mime;
      base64 = match[2];
    }

    // Decodificar base64 → bytes
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);

    // Abrir en nueva pestaña
    window.open(url, "_blank", "noopener,noreferrer");

    // Opcional: liberar memoria luego de un rato
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    console.error("[MODAL][PDF] Error abriendo PDF:", err);
  }
}

function SelectField({
  label,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  readOnly = false,
  help,
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-rose-600">*</span>}
        {readOnly && (
          <span className="text-xs text-gray-400"> (solo lectura)</span>
        )}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || readOnly}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      >
        <option value="">Seleccionar opción</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.nombre}
          </option>
        ))}
      </select>
      {help && <span className="block text-xs text-gray-400 mt-1">{help}</span>}
    </label>
  );
}

export const ModalDeportista = ({
  open,
  athlete,
  posiciones = [],
  grupos = [],
  onCancel,
  onSave,
  saving,
}) => {
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState({
    id: "",
    grupo_id: "",
    posicion_id: "",
    cedula: "",
    nombres: "",
    apellidos: "",
    email: "",
    fecha_nacimiento: "",
    direccion: "",
    telefono: "",
    genero: "Masculino",
    observaciones: "",
    autorizado_dep: null, // empieza siempre sin selección
    observacion_ced_fot: "",
    // nuevos flags para limpiar archivos
    clearFoto: false,
    clearCedulaPdf: false,
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (athlete) {
          setForm({
            id: athlete.id || "",
            grupo_id: athlete.grupo_id || "",
            posicion_id: athlete.posicion_id || "",
            cedula: athlete.cedula || "",
            nombres: athlete.nombres || "",
            apellidos: athlete.apellidos || "",
            email: athlete.email || "",
            fecha_nacimiento: (athlete.fecha_nacimiento || "").slice(0, 10),
            direccion: athlete.direccion || "",
            telefono: athlete.telefono || "",
            genero: athlete.genero || "Masculino",
            observaciones: athlete.observaciones || "",
            // 👇 Siempre arrancamos neutro: ningún botón seleccionado
            autorizado_dep: null,
            observacion_ced_fot: athlete.observacion_ced_fot || "",
            clearFoto: false,
            clearCedulaPdf: false,
          });
        }
      } catch (error) {
        console.error("Error cargando datos del deportista:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
      setPreviewOpen(false);
    }
  }, [athlete, open]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Si rechaza, el comentario es obligatorio
    if (
      form.autorizado_dep === false &&
      (!form.observacion_ced_fot || !form.observacion_ced_fot.trim())
    ) {
      alert(
        "Debes ingresar un comentario explicando el motivo del rechazo de la documentación."
      );
      return;
    }
    onSave(form);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        title="Editar deportista"
        footer={null}
        width={700}
      >
        {loading ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="space-y-1">
                  <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                  <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
              <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
              <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
            </div>
          </div>
        ) : (
          <>
            {athlete && (
              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-3">
                  <img
                    src={athlete.imageUrl}
                    alt={`${athlete.nombres} ${athlete.apellidos}`}
                    className="w-16 h-16 rounded-xl object-cover border border-slate-200 bg-slate-100 cursor-zoom-in"
                    onClick={() => setPreviewOpen(true)}
                    onError={(e) => {
                      console.warn("[MODAL][IMG] error cargando imagen");
                      e.target.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f1f5f9'/%3E%3Cg fill='%233b82f6' transform='translate(100,60)'%3E%3Ccircle cx='0' cy='0' r='18'/%3E%3Crect x='-16' y='18' width='32' height='42' rx='4'/%3E%3Crect x='-20' y='18' width='12' height='35' rx='3'/%3E%3Crect x='8' y='18' width='12' height='35' rx='3'/%3E%3Crect x='-10' y='60' width='8' height='45' rx='3'/%3E%3Crect x='2' y='60' width='8' height='45' rx='3'/%3E%3C/g%3E%3C/svg%3E";
                    }}
                  />
                  <div className="text-xs text-slate-600">
                    <div className="font-semibold text-slate-800">
                      Foto actual del deportista
                    </div>
                    <div className="text-slate-500">
                      Haz clic en la foto para ampliarla y verificar que
                      coincide con la cédula y la información de registro.
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {athlete.cedulaUrl ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 w-28 text-center rounded-lg bg-white border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      onClick={() => {
                        openPdfInNewTab(athlete.cedulaUrl);
                      }}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16"
                        />
                      </svg>
                      Ver cédula
                    </button>
                  ) : (
                    <span className="text-xs text-rose-600">
                      Este deportista todavía no tiene PDF de cédula cargado.
                    </span>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Nombres"
                  value={form.nombres}
                  onChange={(v) => setForm((f) => ({ ...f, nombres: v }))}
                  required
                  disabled={saving}
                />
                <Field
                  label="Apellidos"
                  value={form.apellidos}
                  onChange={(v) => setForm((f) => ({ ...f, apellidos: v }))}
                  required
                  disabled={saving}
                />
                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                  disabled={saving}
                  placeholder="correo@ejemplo.com"
                />
                <Field
                  label="Cédula"
                  value={form.cedula}
                  onChange={(v) => setForm((f) => ({ ...f, cedula: v }))}
                  disabled
                  readOnly
                  placeholder="No disponible"
                />
                <Field
                  label="Teléfono"
                  value={form.telefono}
                  onChange={(v) => setForm((f) => ({ ...f, telefono: v }))}
                  disabled={saving}
                  placeholder="Ej: 0987654321"
                />
                <Field
                  label="Fecha de nacimiento"
                  type="date"
                  value={form.fecha_nacimiento}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, fecha_nacimiento: v }))
                  }
                  disabled
                  readOnly
                />
                <Field
                  label="Dirección"
                  value={form.direccion}
                  onChange={(v) => setForm((f) => ({ ...f, direccion: v }))}
                  disabled={saving}
                  placeholder="Dirección de residencia"
                />
                <SelectField
                  label="Posición"
                  value={form.posicion_id}
                  onChange={(v) => setForm((f) => ({ ...f, posicion_id: v }))}
                  options={posiciones}
                  disabled={saving}
                />
                <label className="block">
                  <span className="block text-xs font-medium text-slate-700 mb-1">
                    Grupo
                  </span>
                  <select
                    value={form.grupo_id ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, grupo_id: e.target.value }))
                    }
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                    required={false}
                  >
                    <option value="">Seleccionar opción</option>
                    {grupos.map((g) => {
                      const category =
                        g.categoria ||
                        g.categoria_nombre ||
                        (g.categoria && g.categoria.nombre) ||
                        "";
                      const label = category
                        ? `${g.nombre} - ${category}`
                        : g.nombre;
                      return (
                        <option key={g.id} value={g.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <SelectField
                  label="Género"
                  value={form.genero}
                  onChange={(v) => setForm((f) => ({ ...f, genero: v }))}
                  options={[
                    { id: "Masculino", nombre: "Masculino" },
                    { id: "Femenino", nombre: "Femenino" },
                    { id: "Otro", nombre: "Otro" },
                  ]}
                  disabled
                  readOnly
                />
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50 text-blue-800 px-3 py-2 text-xs">
                <span className="font-medium">Nota:</span> Los campos cédula,
                fecha de nacimiento y género no pueden editarse.
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Observaciones
                </label>
                <textarea
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                  rows={3}
                  value={form.observaciones}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, observaciones: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="Observaciones adicionales sobre el deportista..."
                />
              </div>

              {/* Revisión de documentación */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 space-y-2">
                <span className="block text-xs font-semibold text-slate-700">
                  Estado de autorización de documentación
                </span>

                {/* Botones inline Autorizar / Rechazar (toggle) */}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setForm((f) => {
                        const nextIsTrue = f.autorizado_dep !== true;
                        return {
                          ...f,
                          autorizado_dep: nextIsTrue ? true : null,
                          // si autoriza, limpiamos comentario
                          observacion_ced_fot: nextIsTrue
                            ? ""
                            : f.observacion_ced_fot,
                        };
                      })
                    }
                    className={clsx(
                      "inline-flex items-center px-3 py-1.5 rounded-full text-xs border transition",
                      form.autorizado_dep === true
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    )}
                  >
                    Autorizar
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        autorizado_dep:
                          f.autorizado_dep === false ? null : false,
                      }))
                    }
                    className={clsx(
                      "inline-flex items-center px-3 py-1.5 rounded-full text-xs border transition",
                      form.autorizado_dep === false
                        ? "bg-rose-600 text-white border-rose-600"
                        : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"
                    )}
                  >
                    Rechazar
                  </button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Puedes seleccionar una sola opción. Si vuelves a hacer clic
                  sobre la misma, se dejará en estado pendiente.
                </p>

                {/* Comentario */}
                <label className="block">
                  <span className="block text-xs font-medium text-slate-700 mb-1">
                    Comentario para el representante{" "}
                    {form.autorizado_dep === false && (
                      <span className="text-rose-600">*</span>
                    )}
                  </span>
                  <textarea
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-xs focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                    rows={3}
                    value={form.observacion_ced_fot}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        observacion_ced_fot: e.target.value,
                      }))
                    }
                    disabled={saving || form.autorizado_dep === true}
                    placeholder={
                      form.autorizado_dep === true
                        ? "No se requiere comentario al autorizar."
                        : "Ej: La foto no es nítida, por favor sube una foto donde se vea claramente el rostro y la cédula."
                    }
                  />
                </label>

                {/* Marcar foto / cédula como incorrectas para borrar archivos */}
                <div className="pt-2 border-t border-slate-200 mt-2 space-y-1.5">
                  <span className="block text-[11px] font-semibold text-slate-700">
                    Archivos a solicitar nuevamente
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        setForm((f) => ({ ...f, clearFoto: !f.clearFoto }))
                      }
                      className={clsx(
                        "inline-flex items-center rounded-full border px-3 py-1 text-[11px] leading-tight transition",
                        form.clearFoto
                          ? "bg-amber-100 text-amber-800 border-amber-300"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                      )}
                    >
                      {form.clearFoto ? "✔ " : ""}
                      Marcar foto como incorrecta
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          clearCedulaPdf: !f.clearCedulaPdf,
                        }))
                      }
                      className={clsx(
                        "inline-flex items-center rounded-full border px-3 py-1 text-[11px] leading-tight transition",
                        form.clearCedulaPdf
                          ? "bg-amber-100 text-amber-800 border-amber-300"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                      )}
                    >
                      {form.clearCedulaPdf ? "✔ " : ""}
                      Marcar cédula (PDF) como incorrecta
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Si marcas estas opciones, se eliminará el archivo actual en
                    el sistema (se guarda como cadena vacía) y el representante
                    deberá subir uno nuevo.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={saving}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && (
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </>
        )}
      </Modal>

      {/* Modal de preview de imagen agrandada */}
      <Modal
        open={previewOpen}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={400}
        centered
      >
        {athlete && (
          <div className="flex flex-col items-center">
            <img
              src={athlete.imageUrl}
              alt={`${athlete.nombres} ${athlete.apellidos}`}
              className="w-full max-w-xs rounded-2xl object-contain border border-slate-200 bg-slate-100"
            />
          </div>
        )}
      </Modal>
    </>
  );
};
